import { plus } from '@0xtorch/big-decimal'
import { type CryptoCurrency, type FiatCurrency, rest } from '@0xtorch/core'
import { z } from 'zod'
import type { AssetBalance } from '../../types'
import { getAsset } from '../../utils/getAsset'
import { bitgetApiEndpoint, bitgetSymbolCryptoIdMap } from '../constants'
import { createPrivateGetHeaders } from '../utils'
import { futureAssetSchema } from './schema'

const responseSchema = z.object({
  code: z.string(),
  msg: z.string(),
  requestTime: z.number().int(),
  data: z.array(futureAssetSchema),
})

const urlPath = '/api/v2/mix/account/accounts'

type FetchFutureAccountAssetsParameters = {
  apiKey: string
  apiSecret: string
  password: string
  proxy?: string
  proxyHeaders?: Record<string, string>
  type: 'USDT-FUTURES' | 'COIN-FUTURES' | 'USDC-FUTURES'
  cryptoCurrencies: readonly CryptoCurrency[]
  fiatCurrencies: readonly FiatCurrency[]
}

export const fetchFutureAccountAssets = async ({
  apiKey,
  apiSecret,
  password,
  proxy,
  proxyHeaders,
  type,
  cryptoCurrencies,
  fiatCurrencies,
}: FetchFutureAccountAssetsParameters): Promise<readonly AssetBalance[]> => {
  const timestamp = Date.now()

  const url = new URL(urlPath, bitgetApiEndpoint)
  url.searchParams.set('productType', type)
  const query = url.searchParams.toString()

  const response = await rest(
    proxy === undefined
      ? url.toString()
      : `${proxy}${encodeURIComponent(url.toString())}`,
    {
      fetchOptions: {
        method: 'GET',
        headers: {
          ...proxyHeaders,
          ...(await createPrivateGetHeaders({
            apiKey,
            apiSecret,
            password,
            urlPath: url.pathname,
            query: query.length > 0 ? `?${query}` : '',
          })),
        },
      },
      schema: responseSchema,
    },
  )

  const assetBalances: AssetBalance[] = []
  for (const assetData of response.data) {
    const asset = getAsset({
      symbol: assetData.marginCoin,
      cryptoCurrencies,
      fiatCurrencies,
      symbolCryptoIdMap: bitgetSymbolCryptoIdMap,
    })
    if (asset === undefined) {
      continue
    }
    const amount = plus(assetData.available, assetData.locked)
    if (amount.value > 0) {
      assetBalances.push({
        key: `bitget_future-balance_${type}`,
        cryptoId: asset.type === 'CryptoCurrency' ? asset.id : undefined,
        fiatId: asset.type === 'FiatCurrency' ? asset.id : undefined,
        amount,
        timestamp,
      })
    }
    if (assetData.unrealizedPL.value !== 0n) {
      assetBalances.push({
        key: `bitget_future-unrealized-pl_${type}`,
        cryptoId: asset.type === 'CryptoCurrency' ? asset.id : undefined,
        fiatId: asset.type === 'FiatCurrency' ? asset.id : undefined,
        amount: assetData.unrealizedPL,
        timestamp,
      })
    }
  }

  return assetBalances
}
